import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { PrivateTrustForm } from '@/components/PrivateTrustForm';
import { Receipt, ArrowRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// Animation component for text appearing line by line
const AnimatedParagraph = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <p
      className={`text-lg text-muted-foreground mb-6 leading-relaxed text-justify transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </p>
  );
};

const PrivateTrustPage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  return (
    <div className="min-h-screen">
      <Header />
      <SEO
        title="Establish a Private Trust | Freeman & Crown Law"
        description="Professional trust formation services for asset protection and estate planning. $5,000 comprehensive trust establishment with expert legal guidance."
        image="/lovable-uploads/8c630ffa-2bac-4996-be73-277885cc03f3.png"
        url={window.location.origin + '/services/private-trust'}
        type="website"
      />
      <section className="pt-32 pb-20 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 rounded-full mb-6">
              <Receipt className="h-8 w-8 text-gold-light" />
            </div>
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-6">
              Establish a Private Trust
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Establish tax-exempt vehicles for private dealings, asset protection, and strategic private operations
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <AnimatedParagraph delay={200}>
              <strong>Quiet structure. Strong protection. Clean operation.</strong>
            </AnimatedParagraph>
            
            <AnimatedParagraph delay={400}>
              At Freeman & Crown Law, we build private trusts designed to hold your assets, keep your affairs private, and make transfers smooth. Within that trust architecture, we also establish and operate a 508(c)(1)(A) convention—so you get the privacy and control of a trust, paired with the federal tax-exempt treatment that applies to a convention or association of churches when run by the book. One integrated plan. No courtroom drama. No late-night tax panic.
            </AnimatedParagraph>

            <h2 className="font-rockwell text-2xl font-semibold text-primary mt-12 mb-6">What this actually means for you</h2>
            
            <AnimatedParagraph delay={600}>
              A private trust becomes the core container. Your real estate, accounts, and other assets are titled to the trust—so when the time comes, they bypass probate and move according to your rules, not the court's. The 508(c)(1)(A) convention operates within that same framework, giving you tax-exempt status for qualifying purpose-related income under the same operational standards used by classic 501(c)(3) organizations. Put bluntly: private where it should be private, exempt where it should be exempt, and documented so it holds up.
            </AnimatedParagraph>

            <h2 className="font-rockwell text-2xl font-semibold text-primary mt-12 mb-6">Why clients choose this model</h2>
            
            <AnimatedParagraph delay={800}>
              <strong>Privacy without theatrics.</strong> The trust keeps sensitive details out of public probate files and creates orderly control over who does what, when, and how.
            </AnimatedParagraph>
            
            <AnimatedParagraph delay={1000}>
              <strong>Protection with substance.</strong> Asset titling, trustee powers, and clean records create real separation—form matching substance, not just fancy paper.
            </AnimatedParagraph>
            
            <AnimatedParagraph delay={1200}>
              <strong>Tax-exempt operations (done right).</strong> A 508(c)(1)(A) convention is recognized as tax-exempt when it follows 501(c)(3) operational rules. That usually means no Form 1023 to apply and no annual Form 990 filings, while staying within clear guardrails. (Unrelated business activity can still be taxed; we design to avoid surprises.)
            </AnimatedParagraph>

            <h2 className="font-rockwell text-2xl font-semibold text-primary mt-12 mb-6">How we build it—simple, calm, complete</h2>
            
            <AnimatedParagraph delay={1400}>
              We start with a plain-English planning call to map your goals, assets, and timeline. Then we draft your trust instrument and the convention governance it supports. We obtain your EIN, open appropriate banking, and give you a funding plan—because if an asset isn't titled to the trust, it won't skip probate. Finally, we hand you a working binder: trustee certificates, minutes templates, ledgers, and a short compliance calendar you can follow without a law degree. You get structure that's elegant on paper and practical in real life.
            </AnimatedParagraph>

            <h2 className="font-rockwell text-2xl font-semibold text-primary mt-12 mb-6">What you receive</h2>
            
            <AnimatedParagraph delay={1600}>
              You'll leave with a fully executed private trust, a documented 508(c)(1)(A) convention operated within that trust framework, banking and EIN setup, and a step-by-step playbook for daily operations—how to take in funds, approve expenditures, minute decisions, and keep the records that make auditors bored and beneficiaries grateful.
            </AnimatedParagraph>

            <h2 className="font-rockwell text-2xl font-semibold text-primary mt-12 mb-6">Plain-talk guardrails</h2>
            
            <AnimatedParagraph delay={1800}>
              <strong>Probate:</strong> Assets titled to the trust avoid probate; assets left outside do not. We handle the retitling plan so the promise matches the outcome.
            </AnimatedParagraph>
            
            <AnimatedParagraph delay={2000}>
              <strong>Tax:</strong> The convention's purpose-related income is tax-exempt when operated within 501(c)(3) rules. If you wander into unrelated business, UBIT can apply. We'll show you the lines on day one.
            </AnimatedParagraph>
            
            <AnimatedParagraph delay={2200}>
              <strong>Formality matters:</strong> Minutes, ledgers, and tidy records aren't busywork—they're how your structure proves itself when challenged.
            </AnimatedParagraph>

            <h2 className="font-rockwell text-2xl font-semibold text-primary mt-12 mb-6">Who this serves best</h2>
            
            <AnimatedParagraph delay={2400}>
              Families, founders, and organizations that want privacy, continuity, and clarity—people who value calm order over chaos, and durable rules over guesswork. If you prefer a smart system that just works, this is your lane.
            </AnimatedParagraph>

            <h2 className="font-rockwell text-2xl font-semibold text-primary mt-12 mb-6">The Freeman & Crown finish</h2>
            
            <AnimatedParagraph delay={2600}>
              Expect clean drafting, crisp funding, and immaculate records—no hype, no gimmicks, no loose ends. We don't sell magic. We build private, durable, audit-ready structures that do exactly what they're supposed to do.
            </AnimatedParagraph>
            
            <AnimatedParagraph delay={2800}>
              <strong>Ready to establish your private trust and 508(c)(1)(A) convention under one roof?</strong><br />
              We'll map it, draft it, fund it, and hand you an operating system you can actually use.
            </AnimatedParagraph>

          {/* Call to Action */}
          <div className="bg-gradient-to-br from-primary/10 to-gold/20 rounded-2xl p-8 md:p-12 text-center border border-gold/30 mt-12">
            <h2 className="font-rockwell text-3xl md:text-4xl font-semibold text-primary mb-6">
              Ready to Establish Your Private Trust?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
              Build your private, durable, audit-ready structure today. We'll map it, draft it, fund it, and hand you an operating system you can actually use.
            </p>
            
            <Collapsible open={isFormOpen} onOpenChange={setIsFormOpen}>
              <CollapsibleTrigger asChild>
                <div className="flex justify-center">
                  <Button 
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3 text-lg"
                  >
                    Start My Trust
                    <ChevronDown className={`ml-2 h-5 w-5 transition-transform ${isFormOpen ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-8">
                <PrivateTrustForm onSuccess={() => setIsFormOpen(false)} />
              </CollapsibleContent>
            </Collapsible>
          </div>
          </div>
        </div>
      </section>

      {/* Educate Yourself Card */}
      <section className="pb-20 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <AnimatedParagraph delay={2600}>
            <Link to="/category/trusts-estate-planning/videos" className="block">
              <div className="p-8 bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl border border-gold/20 hover:border-gold/40 transition-all duration-500 ease-out hover:shadow-lg hover:shadow-gold/20">
                <div className="mb-4">
                  <div className="w-12 h-1 bg-primary mb-6"></div>
                </div>
                <h3 className="font-rockwell text-2xl font-semibold text-primary mb-4 leading-tight">
                  Wanna do for self for free?
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-6 text-sm">
                  We are committed to protecting the constitutional rights of all men and women on the land---and because of this, we make all of our resources and processes completely free so that you are empowered to act on your own without needing anyone or anything or even money for that matter. We give it all for free and if you want to do it that way just click the button below.
                </p>
                <div className="flex justify-start">
                  <Button 
                    variant="link"
                    className="text-primary hover:text-gold p-0 h-auto text-sm font-medium flex items-center gap-1 pointer-events-none"
                  >
                    Educate Myself and Act <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Link>
          </AnimatedParagraph>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default PrivateTrustPage;